import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RotateCcw, Route, Sparkles, CircleCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { postOrQueue } from "@/lib/offlineSync";
import { removeCachedReviewItem } from "@/lib/offlineContent";
import { useAuth } from "@/context/AuthContext";
import { ExerciseRunner } from "@/components/exercises/ExerciseRunner";
import { Celebration } from "@/components/Celebration";

export default function Review() {
  const { lang } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [deck, setDeck] = useState(null);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState(null);
  const [mastered, setMastered] = useState(0);

  const load = useCallback(async () => {
    setSummary(null);
    setMastered(0);
    setRunning(false);
    setDeck((await api.get(`/review/${lang}`)).data);
  }, [lang]);

  useEffect(() => { load(); }, [load]);

  const answer = async (ex, ok) => {
    try {
      const data = await postOrQueue("review_answer", { id: ex.id, correct: ok });
      if (data) {
        setUser(data.user);
        if (data.mastered) setMastered((m) => m + 1);
      } else {
        await removeCachedReviewItem(lang, ex.id);
        if (ok) setUser((u) => ({ ...u, xp: u.xp + 2 }));
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!deck) return null;

  if (running)
    return (
      <div className="px-4 sm:px-6 py-8">
        <ExerciseRunner exercises={deck.items} lang={lang} onResult={answer} onExit={load}
          onFinish={(correct, total) => { setRunning(false); setSummary({ correct, total }); }} />
      </div>
    );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 text-center animate-fade-up" data-testid="review-page">
      <div className="mx-auto h-20 w-20 rounded-3xl bg-[hsl(var(--accent-blue-light))] grid place-items-center mb-6">
        <RotateCcw className="text-[hsl(var(--accent-blue))]" size={34} />
      </div>
      <p className="caption">Daily review</p>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1">Your mistake deck</h1>
      {deck.items.length ? (
        <>
          <p data-testid="review-due-count" className="text-slate-600 mt-3">
            {deck.items.length} item{deck.items.length > 1 ? "s" : ""} due today · {deck.total} in your deck
          </p>
          <p className="text-sm text-slate-500 mt-1">Get an item right on two different days and it leaves the deck for good.</p>
          <button data-testid="review-start-button" onClick={() => setRunning(true)} className="btn-primary mt-8 !px-8">
            <Sparkles size={18} /> Start review
          </button>
        </>
      ) : (
        <div data-testid="review-empty" className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-white p-8">
          <CircleCheck className="mx-auto text-emerald-600 mb-3" size={32} />
          <p className="font-semibold text-slate-800">{deck.total ? "All caught up for today!" : "No mistakes to review yet."}</p>
          <p className="text-sm text-slate-500 mt-1">
            {deck.total ? `${deck.total} item(s) will come back tomorrow.` : "Words you miss in lessons and assessments will show up here."}
          </p>
        </div>
      )}
      <button data-testid="review-path-button" onClick={() => navigate(`/learn/${lang}`)} className="btn-ghost mt-4"><Route size={18} /> Back to path</button>
      <Celebration open={!!summary} onClose={load} title="Review done!" xp={summary ? summary.correct * 2 : 0}
        subtitle={summary && `${summary.correct} of ${summary.total} correct. ${mastered} item(s) mastered and removed.`}>
        <button data-testid="review-done-button" onClick={load} className="btn-primary">Finish</button>
      </Celebration>
    </div>
  );
}
